import * as React from 'react';
import {observer} from 'mobx-react';
import {em, percent, px} from 'csx';
import {colorBackground, colorBrand, colorSubtle, colorWhite} from '../theme/color';
import {classes, style} from 'typestyle';
import {SVGIcon} from './svg-icon';
import {betweenJustified, center, centerCenter, flex1, horizontal, vertical} from 'csstips';
import {constantCreditCardRatio} from '../theme/constant';
import {resetButton} from '../theme/reset';
import {formatCardExpiry, formatObscuredCardNumber} from '../lib/currency';
import {CardSchema} from '../types/schema';
import {noop} from '../lib/noop';

export interface CreditCardModel extends CardSchema {

}

export interface CreditCardProps {
  className?: string;
  model: CreditCardModel;
  selected?: boolean;
  showAddress?: boolean;
  onEditCard?(identity: string): void;
  onRemoveCard?(identity: string): void;
  onSelectCard?(identity: string): void;
  i18n?: {
    edit: string;
    remove: string;
  }
}

const DEFAULT_I18N = {
  edit: 'edit',
  remove: 'remove'
};

@observer
export class CreditCard extends React.Component<CreditCardProps, any> {
  render() {
    const {
      className,
      showAddress,
      model,
      selected,
      onEditCard,
      onRemoveCard,
      onSelectCard,
      i18n = DEFAULT_I18N
    } = this.props;

    const CardIcon = CreditCard.getCardIcon(model.brand);
    const onClick = onSelectCard
      ? (() => onSelectCard(model.identity))
      : noop;

    return (
      <div className={classes(className, CreditCard.styles.container)} onClick={onClick}>
        <div className={classes(CreditCard.styles.inner, selected && CreditCard.styles.innerSelected)}>
          <div className={classes(CreditCard.styles.details, selected && CreditCard.styles.detailsSelected)}>
            <div>{model.name}</div>
            <div>{formatObscuredCardNumber(model)}</div>
            <div>{formatCardExpiry(model)}</div>
          </div>
          <div className={classes(CreditCard.styles.footer, selected && CreditCard.styles.footerSelected)}>
            {!showAddress && (
              <div>
                {onEditCard && (
                  <button
                    className={CreditCard.styles.button}
                    children={i18n.edit}
                    onClick={() => onEditCard(model.identity)}
                  />
                )}
                {onRemoveCard && (
                  <button
                    className={CreditCard.styles.button}
                    children={i18n.remove}
                    onClick={() => onRemoveCard(model.identity)}
                  />
                )}
              </div>
            )}
            {showAddress && (
              <div>
                <div className={CreditCard.styles.address}>
                  <div>{model.address.address1}</div>
                  <div>{model.address.postcode}</div>
                </div>
              </div>
            )}
            <div>
              {CardIcon && (
                <CardIcon size={40} color={colorSubtle.toString()} />
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  static getCardIcon(brand: string) {
    switch (brand) {
      case 'VISA':
        return SVGIcon.Visa;

      case 'MASTERCARD':
        return SVGIcon.MasterCard;

      default:
        return null;
    }
  }

  static styles = {
    container: style({
      position: 'relative',
      $nest: {
        '&:after': {
          content: '""',
          display: 'block',
          paddingTop: percent(100 * constantCreditCardRatio)
        }
      }
    }),
    inner: style({
      ...vertical,
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      border: `1px solid ${colorBackground.fade(0.7).toString()}`,
      background: colorWhite.toString(),
      borderRadius: px(16),
    }),
    innerSelected: style({
      borderWidth: px(2),
      borderColor: colorBrand.toString()
    }),
    details: style({
      ...flex1,
      padding: px(20),
      fontSize: px(16),
      lineHeight: px(28),
      fontWeight: 'bold'
    }),
    detailsSelected: style({
      padding: '19px 19px 20px 19px'
    }),
    footer: style({
      ...horizontal,
      ...betweenJustified,
      padding: px(10),
      $nest: {
        '&> div:first-child': {
          ...horizontal,
          ...center
        },
        '&> div:last-child': {
          ...centerCenter,
          padding: '0 10px'
        }
      }
    }),
    footerSelected: style({
      padding: '10px 9px 9px 9px'
    }),
    button: style({
      ...resetButton,
      color: colorBrand.toString(),
      padding: '5px 10px'
    }),
    address: style({
      fontSize: px(14),
      lineHeight: em(1.2),
      padding: '0 10px',
      color: colorSubtle.toString()
    }),
  }
}

function getCardIcon(brand: string) {
  switch (brand) {
    case 'VISA':
      return SVGIcon.Visa;

    case 'MASTERCARD':
      return SVGIcon.MasterCard;

    default:
      return null;
  }
}